import React, { useMemo } from 'react';
import { Box, Chip, IconButton, Switch, Typography, Stack } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { GoPlus } from 'react-icons/go';
import { useNavigate, useParams } from 'react-router-dom';

import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import RetrievalTestingButton from './RetrievalTestingButton';
import * as urls from 'routes/config';

import { useGetDataSets, useDeleteDataSet, useUpdateDataSet } from 'apis/business/setting/knowledgeBase/hook';

import { formatDate } from 'utils/helperFunctions';
import { DatasetResponse } from 'apis/business/setting/knowledgeBase/types';

type Props = {
  title: string;
  type: 'website' | 'document';
};

export default function DatasetsPartial({ title, type }: Props) {
  const navigate = useNavigate();
  const { currentPage: page, perPage, setPage, setPerPage } = usePagination();
  const { knowledgeBaseId } = useParams();

  const { data: datasetResponse, isLoading } = useGetDataSets({
    page,
    per_page: perPage,
    filter: { datasource_type_eq: type === 'website' ? 1 : 13 }
  });

  const data = datasetResponse?.data ?? [];
  const totalPages = datasetResponse?.meta?.total_pages ?? 1;

  const { mutate: deleteDataset } = useDeleteDataSet();
  const { mutateAsync: updateDataset } = useUpdateDataSet();

  const columns = useColumns({
    onDelete: deleteDataset,
    navigate: navigate,
    onUpdate: updateDataset,
    knowledgeBaseId
  });

  return (
    <>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography variant='h6'>{title}</Typography>

        <Stack direction='row' spacing={3}>
          <RetrievalTestingButton knowledgeBaseId={knowledgeBaseId} />

          <Button startIcon={<GoPlus />} onClick={() => navigate(type === 'website' ? 'add-website' : 'add')}>
            Add New
          </Button>
        </Stack>
      </Box>

      <DataTable data={data} columns={columns} isHeaderSticky isLoading={isLoading} />
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalCount={datasetResponse?.meta?.total_count ?? 0}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />
    </>
  );
}

function useColumns({
  onDelete,
  navigate,
  onUpdate,
  knowledgeBaseId
}: {
  onDelete: (id: string) => void;
  navigate: any;
  onUpdate: (payload: any) => void;
  knowledgeBaseId?: string;
}) {
  return useMemo<ColumnDef<DatasetResponse>[]>(
    () => [
      {
        header: 'Page Title',
        size: 80,
        accessorKey: 'name'
      },
      {
        header: 'Page Url',
        size: 80,
        accessorKey: 'page_url'
      },
      {
        header: 'Questions',
        accessorKey: 'chunks_count'
      },
      {
        header: 'Created At',
        cell: (info) => formatDate(info.row.original.created_at)
      },
      {
        header: 'ENABLE',
        cell: (info) => (
          <Switch
            checked={Boolean(info.row.original.enable)}
            onChange={(e) =>
              onUpdate({
                datasetId: info.row.original.id,
                payload: { dataset: { enable: e.target.checked } }
              })
            }
          />
        )
      },
      {
        header: 'STATUS',
        cell: (info) => (
          <Chip
            label={info.row.original.status}
            variant='outlined'
            color={pillColor[info.row.original.status || 'failed']}
            sx={{
              paddingY: '4px',
              paddingX: '12px',
              cursor: 'help',
              borderRadius: '100px'
            }}
          />
        )
      },
      {
        header: 'Action',
        cell: (info) => (
          <Box display='flex' gap={1}>
            <IconButton
              onClick={() =>
                navigate(
                  `${urls.business.base}/${urls.business.KnowledgeBases}/${knowledgeBaseId}/dataset/${urls.knowledgeBase.chunks(info.row.original.id)}`
                )
              }
            >
              <MdOutlineEdit />
            </IconButton>

            <IconButton color='error' onClick={() => onDelete(String(info.row.original.id))}>
              <RiDeleteBin6Line />
            </IconButton>
          </Box>
        )
      }
    ],
    [onDelete, onUpdate, knowledgeBaseId, navigate]
  );
}

const pillColor: Record<string, 'success' | 'warning' | 'error'> = {
  parsing: 'warning',
  success: 'success',
  failed: 'error',
  cancelled: 'error'
};
